import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import OrganisationProjectCard from '../../components/projects/OrganisationProjectCard';
import {
  PageContainer,
  PageToolbar,
  EmptyState,
  ToolbarDivider,
} from '../../components/ui/PageLayout';
import { useOrganisationName } from '../../hooks/useOrganisationName';
import { getOrgId, streamsAPI, projectsAPI } from '../../services/api';
import toast from '../../services/toast';
import AssignProjectMemberModal from '../ProjectDetail/AssignProjectMemberModal';
import { FolderKanban } from 'lucide-react';
import SearchBar from '../../components/ui/SearchBar';
import { PageToolbarSkeleton, CardGridSkeleton } from '../../components/ui/Skeleton';

const Projects = () => {
  const navigate = useNavigate();
  const orgName = useOrganisationName();
  const [orgId, setOrgId] = useState<any>(null);
  const [streams, setStreams] = useState<any[]>([]);
  const [projectsWithStream, setProjectsWithStream] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignMemberProject, setAssignMemberProject] = useState<any>(null);
  const [search, setSearch] = useState('');

  const filteredProjects = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return projectsWithStream;
    return projectsWithStream.filter(
      (p) =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.streamName && p.streamName.toLowerCase().includes(q))
    );
  }, [projectsWithStream, search]);

  useEffect(() => {
    getOrgId()
      .then((id) => {
        setOrgId(id);
        return id;
      })
      .then((id) => (id ? streamsAPI.list(id) : []))
      .then((list) => setStreams(Array.isArray(list) ? list : []))
      .catch((err) => {
        toast.error((err as Error).message || 'Failed to load streams');
        setStreams([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!orgId || streams.length === 0) {
      setProjectsWithStream([]);
      return;
    }
    const fetchAll = async () => {
      const results: any[] = [];
      for (const stream of streams) {
        try {
          const projects = await projectsAPI.listByStream(orgId, stream.id, { stats: false });
          (projects || []).forEach((p) => {
            results.push({ ...p, streamName: stream.name, streamId: stream.id });
          });
        } catch {
          // skip this stream
        }
      }
      setProjectsWithStream(results);
    };
    fetchAll();
  }, [orgId, streams]);

  const refetchProjects = () => {
    if (!orgId || streams.length === 0) return;
    const fetchAll = async () => {
      const results: any[] = [];
      for (const stream of streams) {
        try {
          const projects = await projectsAPI.listByStream(orgId, stream.id, { stats: false });
          (projects || []).forEach((p) => {
            results.push({ ...p, streamName: stream.name, streamId: stream.id });
          });
        } catch {
          // skip
        }
      }
      setProjectsWithStream(results);
    };
    fetchAll();
  };

  if (loading) {
    return (
      <PageContainer>
        <PageToolbarSkeleton />
        <div className="border-t border-dark-border my-4" />
        <CardGridSkeleton count={6} />
      </PageContainer>
    );
  }

  if (!orgId) {
    return (
      <PageContainer>
        <PageToolbar title="Projects" />
        <p className="text-slate-400">No organisation found. Please complete setup.</p>
      </PageContainer>
    );
  }

  const orgLabel = orgName || 'Organisation';
  const projectsBreadcrumb = [{ label: orgLabel, to: '/' }, { label: 'Projects' }];

  if (streams.length === 0) {
    return (
      <PageContainer>
        <PageToolbar title="Projects" breadcrumbItems={projectsBreadcrumb} />
        <ToolbarDivider />
        <EmptyState
          icon={<FolderKanban className="w-12 h-12 text-slate-500" />}
          title="Create a stream first."
          description="Add a stream from Configuration → System."
          action={
            <button
              type="button"
              onClick={() => navigate('/configuration/system')}
              className="btn-primary-mdl"
            >
              Open Configuration
            </button>
          }
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageToolbar title="Projects" breadcrumbItems={projectsBreadcrumb}>
        <div className="w-64 max-w-[50vw]">
          <SearchBar value={search} onChange={setSearch} placeholder="Search projects…" />
        </div>
      </PageToolbar>
      <ToolbarDivider />

      {filteredProjects.length === 0 ? (
        projectsWithStream.length > 0 ? (
          <p className="text-sm text-slate-400">No projects match your search.</p>
        ) : (
          <EmptyState
            icon={<FolderKanban className="w-12 h-12 text-slate-500" />}
            title="No projects yet."
            description="Add projects from Configuration → System."
            action={
              <button
                type="button"
                onClick={() => navigate('/configuration/system')}
                className="btn-primary-mdl"
              >
                Open Configuration
              </button>
            }
          />
        )
      ) : (
        <div className="cards-grid-mdl-dense">
          {filteredProjects.map((p) => (
            <OrganisationProjectCard
              key={p.id}
              orgId={orgId}
              streamId={p.streamId}
              project={p}
              streamLabel={p.streamName}
            />
          ))}
        </div>
      )}

      <AssignProjectMemberModal
        isOpen={!!assignMemberProject}
        onClose={() => setAssignMemberProject(null)}
        orgId={orgId}
        streamId={assignMemberProject?.streamId}
        projectId={assignMemberProject?.id}
        projectName={assignMemberProject?.name}
        onSuccess={refetchProjects}
      />
    </PageContainer>
  );
};

export default Projects;
